// src/utils/analytics.ts

import { ref, readonly } from 'vue';

// 聲明 dataLayer
declare global {
  interface Window {
    dataLayer?: any[];
  }
}

// --- Interfaces (No changes needed) ---
export interface ToolUsageStats {
  toolName: string;
  usageCount: number;
  totalTime: number;
  successRate: number;
  lastUsed: Date;
}
export interface AdStats {
    adSlot: string
    adSize: string
    adProvider: string
    views: number
    clicks: number
    errors: number
    ctr: number
    revenue: number
    lastViewed: Date
}
export interface UserBehavior {
    sessionId: string
    userId?: string
    language: string
    userAgent: string
    referrer: string
    timestamp: Date
}
export interface ErrorReport {
  message: string;
  stack?: string;
  url: string;
  lineNumber?: number;
  columnNumber?: number;
  timestamp: Date;
  userAgent: string;
  userId?: string;
}


// --- Global State (No changes needed) ---
const isAnalyticsEnabled = ref(true);
const sessionId = ref(generateSessionId());

function generateSessionId(): string {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// --- Core GTM DataLayer Function ---
function pushToDataLayer(data: Record<string, any>) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
  console.log('📊 GTM DataLayer Push:', data);
}

// --- Initialization ---
export function initializeAnalytics() {
  const consent = localStorage.getItem('ads-consent');
  if (consent === 'false') {
    isAnalyticsEnabled.value = false;
    return;
  }
  // GTM is already loaded via index.html, so we just need to set up error tracking.
  setupErrorTracking();
  console.log('✅ Analytics (GTM) initialized.');
}

// --- Page View Tracking ---
export function trackPageView(pagePath?: string, pageTitle?: string) {
  if (!isAnalyticsEnabled.value) return;

  const path = pagePath || window.location.pathname;
  const title = pageTitle || document.title;

  pushToDataLayer({
    event: 'page_view',
    page_title: title,
    page_location: window.location.origin + path,
    page_path: path,
    language: getCurrentLanguage(),
    app_name: 'CallMeAPI',
    app_version: '1.0.0',
    user_agent: navigator.userAgent,
    screen_resolution: `${screen.width}x${screen.height}`,
  });
}

// --- Event Tracking ---

export async function trackToolUsage(
  toolName: string,
  executionTimeOrAction?: number | string,
  success: boolean = true,
  additionalData?: Record<string, any>
) {
  if (!isAnalyticsEnabled.value) return;

  // 支持兩種調用方式：
  // 1. trackToolUsage('tool-name', executionTime, success)
  // 2. trackToolUsage('tool-name', 'action') - 純前端工具
  let executionTime = 0;
  let action = '';

  if (typeof executionTimeOrAction === 'number') {
    executionTime = executionTimeOrAction;
  } else if (typeof executionTimeOrAction === 'string') {
    action = executionTimeOrAction;
  }

  // 推送到 GTM DataLayer
  pushToDataLayer({
    event: 'tool_usage',
    tool_name: toolName,
    execution_time: executionTime,
    success: success,
    action: action,
    ...additionalData,
  });

  // 更新本地統計
  updateLocalStats(toolName, executionTime, success);

  // 發送到後端 API 進行統一統計（容錯處理）
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';
    await fetch(`${apiBaseUrl}/api/analytics/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        toolName,
        executionTime,
        success,
        action,
      }),
    });
  } catch (error) {
    // 靜默失敗，統計錯誤不影響工具使用
    console.debug('Analytics tracking failed:', error);
  }
}

export function trackUserBehavior(details?: Record<string, any>) {
    if (!isAnalyticsEnabled.value) return;

    pushToDataLayer({
        event: 'user_behavior',
        session_id: sessionId.value,
        language: navigator.language,
        user_agent: navigator.userAgent,
        referrer: document.referrer,
        ...details,
    });
}


export function trackLanguageChange(fromLang: string, toLang: string) {
  if (!isAnalyticsEnabled.value) return;

  pushToDataLayer({
    event: 'language_change',
    from_language: fromLang,
    to_language: toLang,
  });
}

export function trackAdClick(adProvider: string, adSlot: string, adSize?: string) {
  if (!isAnalyticsEnabled.value) return;

  pushToDataLayer({
    event: 'ad_click',
    ad_provider: adProvider,
    ad_slot: adSlot,
    ad_size: adSize || 'unknown',
    value: getAdClickValue(adSize),
    language: getCurrentLanguage(),
    page_title: document.title,
  });

  updateLocalAdStats(adSlot, adSize || 'unknown', adProvider, 'click');
}

export function trackAdError(adSlot: string, errorMessage: string, adProvider: string) {
  if (!isAnalyticsEnabled.value) return;

  pushToDataLayer({
    event: 'ad_error',
    ad_provider: adProvider,
    ad_slot: adSlot,
    error_message: errorMessage,
    language: getCurrentLanguage(),
  });

  updateLocalAdStats(adSlot, 'unknown', adProvider, 'error');
}

export function trackAdLoadTime(adSlot: string, loadTime: number, adProvider: string) {
    if (!isAnalyticsEnabled.value) return;
    pushToDataLayer({
        event: 'ad_load_time',
        ad_slot: adSlot,
        load_time: loadTime,
        ad_provider: adProvider,
    });
}

export function trackAdVisibility(adSlot: string, visibilityPercentage: number, adProvider: string) {
    if (!isAnalyticsEnabled.value) return;
    pushToDataLayer({
        event: 'ad_visibility',
        ad_slot: adSlot,
        visibility_percentage: visibilityPercentage,
        ad_provider: adProvider,
    });
}

export function trackAdABTest(testName: string, variant: string, adSlot: string) {
    if (!isAnalyticsEnabled.value) return;
    pushToDataLayer({
        event: 'ad_ab_test',
        test_name: testName,
        variant: variant,
        ad_slot: adSlot,
    });
}

export function trackAdRevenue(adSlot: string, revenue: number, adProvider: string) {
    if (!isAnalyticsEnabled.value) return;
    pushToDataLayer({
        event: 'ad_revenue',
        ad_slot: adSlot,
        revenue: revenue,
        currency: 'USD', // Assuming USD
        ad_provider: adProvider,
    });
}


export function trackError(error: Error, context?: string, fatal: boolean = false) {
  if (!isAnalyticsEnabled.value) return;

  pushToDataLayer({
    event: 'exception',
    description: `${context ? `[${context}] ` : ''}${error.message}`,
    fatal: fatal,
  });

  console.error('Tracked error:', error);
}

function setupErrorTracking() {
  window.addEventListener('error', (event) => {
    trackError(new Error(event.message), 'Global Error Handler', true);
  });

  window.addEventListener('unhandledrejection', (event) => {
    trackError(new Error(event.reason), 'Unhandled Promise Rejection', true);
  });
}

export function trackInteraction(interactionType: string, elementId?: string, value?: any) {
  if (!isAnalyticsEnabled.value) return;

  pushToDataLayer({
    event: 'user_interaction',
    interaction_type: interactionType,
    element_id: elementId,
    value: value,
  });
}


// --- Helper Functions & Local Storage (No changes needed in logic) ---

function getCurrentLanguage(): string {
  return document.documentElement.lang || 'zh-TW';
}

function getAdClickValue(adSize: string = 'unknown'): number {
  const valueMap: Record<string, number> = {
    'banner': 0.5,
    'medium-rectangle': 1.0,
    'leaderboard': 0.8,
    'skyscraper': 0.6,
    'mobile-banner': 0.3,
    'auto': 0.7,
    'unknown': 0.5
  };
  return valueMap[adSize] || 0.5;
}

function updateLocalStats(toolName: string, executionTime: number, success: boolean) {
  const statsKey = 'tool_usage_stats'
  const existingStats = JSON.parse(localStorage.getItem(statsKey) || '{}')
  
  if (!existingStats[toolName]) {
    existingStats[toolName] = {
      toolName,
      usageCount: 0,
      totalTime: 0,
      successCount: 0,
      lastUsed: new Date().toISOString()
    }
  }

  const stats = existingStats[toolName]
  stats.usageCount++
  stats.totalTime += executionTime
  if (success) stats.successCount++
  stats.lastUsed = new Date().toISOString()
  stats.successRate = (stats.successCount / stats.usageCount) * 100

  localStorage.setItem(statsKey, JSON.stringify(existingStats))
}

function updateLocalAdStats(adSlot: string, adSize: string, adProvider: string, action: 'view' | 'click' | 'error') {
  try {
    const statsKey = 'ad-stats'
    const existing = localStorage.getItem(statsKey)
    const stats: Record<string, AdStats> = existing ? JSON.parse(existing) : {}
    
    const key = `${adProvider}_${adSlot}`
    
    if (!stats[key]) {
      stats[key] = {
        adSlot,
        adSize,
        adProvider,
        views: 0,
        clicks: 0,
        errors: 0,
        ctr: 0,
        revenue: 0,
        lastViewed: new Date()
      }
    }

    const stat = stats[key]
    
    switch (action) {
      case 'view':
        stat.views++
        stat.lastViewed = new Date()
        break
      case 'click':
        stat.clicks++
        stat.revenue += getAdClickValue(adSize)
        break
      case 'error':
        stat.errors++
        break
    }

    stat.ctr = stat.views > 0 ? (stat.clicks / stat.views) * 100 : 0

    stats[key] = stat
    localStorage.setItem(statsKey, JSON.stringify(stats))
  } catch (error) {
    console.warn('Failed to update ad stats:', error)
  }
}

export function getAdStats(): Record<string, AdStats> {
  try {
    const statsKey = 'ad-stats'
    const existing = localStorage.getItem(statsKey)
    return existing ? JSON.parse(existing) : {}
  } catch (error) {
    console.warn('Failed to get ad stats:', error)
    return {}
  }
}

export function clearAdStats() {
  try {
    localStorage.removeItem('ad-stats')
  } catch (error) {
    console.warn('Failed to clear ad stats:', error)
  }
}

export function getAdStatsSummary() {
  const stats = getAdStats()
  const summary = {
    totalViews: 0,
    totalClicks: 0,
    totalErrors: 0,
    averageCTR: 0,
    totalRevenue: 0,
    topPerformingAds: [] as Array<{ slot: string, ctr: number, revenue: number }>
  }

  const values = Object.values(stats)
  
  if (values.length === 0) return summary

  summary.totalViews = values.reduce((sum, stat) => sum + stat.views, 0)
  summary.totalClicks = values.reduce((sum, stat) => sum + stat.clicks, 0)
  summary.totalErrors = values.reduce((sum, stat) => sum + stat.errors, 0)
  summary.totalRevenue = values.reduce((sum, stat) => sum + stat.revenue, 0)
  summary.averageCTR = summary.totalViews > 0 ? (summary.totalClicks / summary.totalViews) * 100 : 0

  summary.topPerformingAds = values
    .map(stat => ({
      slot: stat.adSlot,
      ctr: stat.ctr,
      revenue: stat.revenue
    }))
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5)

  return summary
}

export function getLocalStats(): Record<string, ToolUsageStats> {
  const statsKey = 'tool_usage_stats';
  return JSON.parse(localStorage.getItem(statsKey) || '{}');
}

export function clearStats() {
  localStorage.removeItem('tool_usage_stats');
}

export function setAnalyticsConsent(consent: boolean) {
  isAnalyticsEnabled.value = consent;
  localStorage.setItem('ads-consent', consent ? 'true' : 'false');
  if (consent) {
    initializeAnalytics();
  }
}

// --- Exports ---
export default {
  initializeAnalytics,
  trackToolUsage,
  trackUserBehavior,
  trackPageView,
  trackLanguageChange,
  trackAdClick,
  trackAdError,
  trackAdLoadTime,
  trackAdVisibility,
  trackAdABTest,
  trackAdRevenue,
  trackError,
  trackInteraction,
  getLocalStats,
  clearStats,
  setAnalyticsConsent,
  getAdStats,
  clearAdStats,
  getAdStatsSummary,
  isAnalyticsEnabled: readonly(isAnalyticsEnabled),
}; 